import "@tento-ui/components/icon-button";
import "@tento-ui/components/home-card/row-icon";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { IconButton } from "@tento-ui/components/icon-button";
import { RowIcon } from "@tento-ui/components/home-card/row-icon";
import { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import { type SheetRow } from "@tento-lona";
import { TimelineLayoutManager } from "@tento-ui/components/timeline/timeline-layout-manager";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { ExpandIcon, LonaIcons } from "@tento-ui/ui/icons";
import { EventItemAllDay, type Option } from "@tento-lona/sheets";
import { AllDayEventGhost } from "@tento-lona/sheets-ui/sheet/gestures/all-day-event-ghost.ts";
import { AllDayEventInfo } from "@tento-lona/sheets-ui/sheet/gestures/all-day-event-info.ts";

@component({
  name: "all-day-event-legend",
})
export class AllDayEventLegend extends LonaWebComponent {
  $toggleBtn: IconButton = this.hydrate("toggle-btn");
  $icon: RowIcon = this.hydrate("icon");
  #manager: Option<TimelineLayoutManager<EventItemAllDay, AllDayEventGhost, AllDayEventInfo>> = null;

  constructor() {
    super();
    this.$toggleBtn
      .bind({
        content: LonaIcons.$svg(ExpandIcon),
        squarish: true,
        sizePx: 20,
      })
      .assignStyles({
        "--std-icon-button-color": "var(--fg-icon-pri)",
      });
  }

  bind(
    row: SheetRow.Joined.Handle,
    handle: SheetDelegate,
    manager: TimelineLayoutManager<
      EventItemAllDay,
      AllDayEventGhost,
      AllDayEventInfo
    >,
  ) {
    // Cleanup previous GESTURE_MANAGER handlers before rebinding
    GESTURE_MANAGER.unregister(this.$("icon"));
    GESTURE_MANAGER.unregister(this.$("label-container"));
    GESTURE_MANAGER.unregister(this.$toggleBtn);

    this.setAttribute("row-id", row.id.toString());
    this.clearChildren();

    // Remove listener from previous manager before adding new one
    this.#manager?.maxHeight.removeListener(this);
    this.#manager = manager;
    manager.maxHeight.addListener(this, (_m, _prev) => {
      // Occupied lanes changed. When expanded, row height tracks
      // occupiedRows (`effectiveVisibleRows`), so re-read heights.
      if (manager.expanded) handle.rebind.invalidateRowHeight();
    });

    this.$icon.bind(row, { icon: handle.pluginManager.get(row).getIcon() });
    this.$("label").textContent = row.label ?? row.labelPlaceholder;

    GESTURE_MANAGER.addPointerEvent(this.$("icon"), {
      onClick: () => handle.rebind.revealRowOptionsPopover(row, "emoji"),
      onContextMenu: () => handle.rebind.revealRowOptionsPopover(row, "emoji"),
    });

    GESTURE_MANAGER.addPointerEvent(this.$("label-container"), {
      onClick: () => handle.rebind.revealRowOptionsPopover(row, "default"),
      onContextMenu: () =>
        handle.rebind.revealRowOptionsPopover(row, "default"),
    });

    GESTURE_MANAGER.addPointerEvent(this.$toggleBtn, {
      onClick: () => {
        manager.setExpanded(!manager.expanded);
        // Heights: the row's layoutSpec (TimelineRowLayoutPlugin's descriptor
        // override) re-reads `effectiveVisibleRows`. Viewports: re-run
        // fixedChildrenForRow so lanes revealed/hidden by the toggle
        // paint their bars and the more-indicator moves.
        handle.rebind.invalidateRowHeight();
        handle.rebind.rebindViewports();
      },
    });
  }

  disconnectedCallback() {
    this.#manager?.maxHeight.removeListener(this);
  }

  static $styles: Style.Compat[] = [
    SheetLegend.DefaultRow.$labelContainerStyle,
    css`
      :host {
        width: 100%;
      }

      #root {
        height: 100%;
        width: 100%;
        padding-right: 8px;
        align-items: center;
      }

      #toggle-btn {
        margin-top: -1px;
        margin-left: 8px;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <std-row style=height:32px;align-items:center;width:100%;>
        <std-row-icon id=icon emoji-target></std-row-icon>
        <std-row id="label-container" label-target>
          <p id="label" class="small">timeline</p>
        </std-row>
        <std-icon-button id="toggle-btn"></std-icon-button>
      </std-row>
    </std-col>
  `;
}
